import React from 'react';
import { MODE_NAMES } from '../../lib/theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  Tray, ControlCard, TrayLabel, KeyRows, StringsCard, StandardQuickButtons,
} from '../shared';

// ── Data ──────────────────────────────────────────────────────────────────────
// The redesign folds the old Category (SCALES / CHORDS) and Type rows into one Structure
// tray: Scale / Penta / Modes / Chords. App.js still stores dobroCategory + dobroType (and the
// fretboard reads them), so the tray values below are mapped onto that state:
//   Full Scale | Pentatonic | Modes -> dobroCategory 'SCALES', dobroType = the tray value
//   Chords                          -> dobroCategory 'CHORDS', dobroType 'Straight Barre'

/** Structure labels: board 18 (phone) uses the short ones, board 20 (desktop) spells them out. */
const STRUCTURE_OPTIONS_PHONE = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Penta' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Chords', label: 'Chords' },
];
const STRUCTURE_OPTIONS_DESKTOP = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Pentatonic' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Chords', label: 'Chords' },
];

/** Strings low to high (open G tuning), matching dobroStringFocus. */
const STRINGS = [
  { label: 'G', ariaLabel: 'Low G string' },
  { label: 'B', ariaLabel: 'Low B string' },
  { label: 'D', ariaLabel: 'Low D string' },
  { label: 'G', ariaLabel: 'High G string' },
  { label: 'B', ariaLabel: 'High B string' },
  { label: 'D', ariaLabel: 'High D string' },
];

/**
 * Structure tray value for the stored dobro state.
 * @param {string} category dobroCategory ('SCALES' | 'CHORDS')
 * @param {string} type     dobroType ('Full Scale' | 'Pentatonic' | 'Modes' | 'Straight Barre')
 * @returns {'Full Scale'|'Pentatonic'|'Modes'|'Chords'}
 */
export function dobroStructureOf(category, type) {
  return category === 'CHORDS' ? 'Chords' : type;
}

/**
 * Stored dobro state for a Structure tray value (the inverse of {@link dobroStructureOf}).
 * @param {string} structure
 * @returns {{category: string, type: string, shape: string}}
 */
export function dobroStateFromStructure(structure) {
  if (structure === 'Chords') return { category: 'CHORDS', type: 'Straight Barre', shape: 'Straight Barre' };
  return { category: 'SCALES', type: structure, shape: 'All Shapes' };
}

// ── Controls: Key card + Strings card ─────────────────────────────────────────

/**
 * Dobro controls (board 18 phone / board 20 desktop): the Key and Strings cards.
 * The quick buttons live in {@link DobroQuickButtons}; the page layout puts them under the cards.
 * Chords has only Straight Barre, so it needs no follow-up tray; Modes opens the mode tray.
 *
 * @param {object} props
 * @param {string} props.dobroStructure Tray value, from {@link dobroStructureOf}.
 * @param {(structure: string) => void} props.onDobroStructureChange
 */
export default function DobroControls({
  dobroKey,         onDobroKeyChange,
  dobroTonality,    onDobroTonalityChange,
  dobroStructure,   onDobroStructureChange,
  dobroMode,        onDobroModeChange,
  dobroStringFocus, onDobroStringToggle,
}) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'md' : 'sm';
  const isModes = dobroStructure === 'Modes';

  return (
    <>
      <ControlCard title="Key" size={size}>
        <KeyRows
          isDesktop={isDesktop}
          keyValue={dobroKey}
          onKeyChange={onDobroKeyChange}
          tonality={dobroTonality}
          onTonalityChange={onDobroTonalityChange}
          // A mode sets its own quality, so Major/Minor sits out while Modes is on.
          tonalityDisabled={isModes}
          structureOptions={STRUCTURE_OPTIONS_PHONE}
          structureOptionsDesktop={STRUCTURE_OPTIONS_DESKTOP}
          structure={dobroStructure}
          onStructureChange={onDobroStructureChange}
        />

        {isModes && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Mode</TrayLabel>}
            <Tray
              ariaLabel="Mode"
              options={MODE_NAMES}
              value={dobroMode}
              onChange={onDobroModeChange}
              columns={isDesktop ? 3 : 4}
            />
          </>
        )}
      </ControlCard>

      <StringsCard strings={STRINGS} focus={dobroStringFocus} onToggle={onDobroStringToggle} size={size} />
    </>
  );
}

// ── Quick buttons: Filter / Labels / Reset ────────────────────────────────────

/**
 * The three quick buttons for the layout's `quickButtons` slot.
 * Filter cycles All Notes -> Triads Only -> Roots Only and is greyed for Chords (every chord tone
 * always shows). Labels toggles Note Names <-> Intervals ('None' counts as Names).
 */
export function DobroQuickButtons({
  dobroStructure,
  dobroFilter, onDobroFilterChange,
  dobroLabel,  onDobroLabelChange,
  onDobroReset,
}) {
  const isDesktop = useIsDesktop();
  return (
    <StandardQuickButtons
      size={isDesktop ? 'md' : 'sm'}
      filter={dobroFilter}
      onFilterChange={onDobroFilterChange}
      filterDisabled={dobroStructure === 'Chords'}
      label={dobroLabel}
      onLabelChange={onDobroLabelChange}
      onReset={onDobroReset}
    />
  );
}
